import { ExternalLink } from "@/components/ExternalLink";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { Clients } from "@/lib/clients";
import { createEnum } from "@/lib/utils/createEnum";

export type E2Position = {
  id: number;
  strategy_from_id?: number | null;
  strategy_from_name?: string | null;
  strategy_to_id?: number | null;
  strategy_to_name?: string | null;
  quantity?: number | null;
  date?: string | null;
  weight?: number | null;
  market_value_local_ccy?: number | null;
  market_value_ptf_ccy?: number | null;
  time_serie_file_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export const e2PositionFields = createEnum<E2Position>();

const tableName = `${Clients.E2}_positions`;
const t = (key: string, defaultValue: string) =>
  i18n.t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });

export const e2_positions_columns: ColumnDef<E2Position>[] = [
  {
    accessorKey: e2PositionFields.id,
    header: () => t(e2PositionFields.id, "ID"),
  },
  {
    accessorKey: e2PositionFields.strategy_from_name,
    header: () => t(e2PositionFields.strategy_from_name, "Portefeuille"),
    cell: ({ row }) => {
      const id = row.original.strategy_from_id;
      const name = row.original.strategy_from_name;
      if (!id || !name) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: e2PositionFields.strategy_to_name,
    header: () => t(e2PositionFields.strategy_to_name, "Stratégie"),
    cell: ({ row }) => {
      const id = row.original.strategy_to_id;
      const name = row.original.strategy_to_name;
      if (!id || !name) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: e2PositionFields.date,
    header: () => t(e2PositionFields.date, "Date"),
    cell: ({ row }) => {
      const value = row.getValue(e2PositionFields.date);
      return value ? parseDate(String(value)) : "";
    },
  },
  {
    accessorKey: e2PositionFields.quantity,
    header: () => (
      <div className="text-right">
        {t(e2PositionFields.quantity, "Quantity")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(e2PositionFields.quantity))}
      </div>
    ),
  },
  {
    accessorKey: e2PositionFields.weight,
    header: () => (
      <div className="text-right">{t(e2PositionFields.weight, "Weight")}</div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(e2PositionFields.weight))}
      </div>
    ),
  },
  {
    accessorKey: e2PositionFields.market_value_local_ccy,
    header: () => (
      <div className="text-right">
        {t(
          e2PositionFields.market_value_local_ccy,
          "Market value (local currency)",
        )}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(e2PositionFields.market_value_local_ccy))}
      </div>
    ),
  },
  {
    accessorKey: e2PositionFields.market_value_ptf_ccy,
    header: () => (
      <div className="text-right">
        {t(
          e2PositionFields.market_value_ptf_ccy,
          "Market value (portfolio currency)",
        )}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(e2PositionFields.market_value_ptf_ccy))}
      </div>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(e2PositionFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
